import { asAccountId } from "@repo/app-types/entities/account";
import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import {
  CustomerTimeline,
  CustomerTimelineSkeleton,
  type CustomerTimelineActivity,
} from "@repo/ui/customerTimeline/customerTimeline";
import { CustomerTimelineCall } from "@repo/ui/customerTimeline/customerTimelineCall";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { useInfiniteScroll } from "@repo/ui/helpers/useInfiniteScroll";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { OutcomeResultPill } from "@repo/ui/Pills/outcomeResultPill";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { teammateLabelKind } from "@repo/ui/teammate/teammateLabel";
import { IconPhone } from "@tabler/icons-react";
import { DateTime } from "luxon";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
  type RefObject,
} from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  useCustomerDetailQuery,
  useCustomerTimelineInfiniteQuery,
} from "../../api/entities/customer.js";
import { appPaths } from "../../app/appPaths.js";
import { requireRouteParam } from "../../app/routeParams.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { renderCallLength } from "../teammatePerformance/durationFormatting.js";
import { GradingSectionSummariesContainer } from "../teammatePerformance/gradingSectionSummariesContainer.js";
import { CustomerInfoPopover } from "./customerInfoPopover.js";
import { CustomerPlaybookScores } from "./customerPlaybookScores.js";

const SCORE_CARD_ENTRY_DURATION_MS = 300;
const SCORE_CARD_START_DELAY_MS = 300;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface ScoreCardEntryProps {
  timelineRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}

const ScoreCardEntry: FC<ScoreCardEntryProps> = (props) => {
  const { timelineRef, children } = props;
  const [isInteractive, setIsInteractive] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const card = cardRef.current;
    const timeline = timelineRef.current;
    if (!card || !timeline) {
      return;
    }
    if (prefersReducedMotion() || !timeline.animate) {
      setIsInteractive(true);
      return;
    }

    // The card takes its final layout space once; only the timeline moves per frame.
    const distance =
      timeline.getBoundingClientRect().top - card.getBoundingClientRect().top;
    const animation = timeline.animate(
      [
        { transform: `translateY(-${distance}px)` },
        { transform: "translateY(0)" },
      ],
      {
        duration: SCORE_CARD_ENTRY_DURATION_MS,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      },
    );
    animation.onfinish = () => setIsInteractive(true);
    return () => {
      animation.onfinish = null;
      animation.cancel();
    };
  }, [timelineRef]);

  return (
    <div
      ref={cardRef}
      inert={!isInteractive}
      className="mb-4 motion-safe:animate-[fade-in_200ms_ease-in-out_100ms_both] sm:mb-6"
    >
      {children}
    </div>
  );
};

interface CustomerDetailContentProps {
  isTimelineLoading: boolean;
  isError: boolean;
  scoreCard: ReactNode;
  timeline: ReactNode;
}

const CustomerDetailContent: FC<CustomerDetailContentProps> = (props) => {
  const { isTimelineLoading, isError, scoreCard, timeline } = props;
  const hasScoreCard = Boolean(scoreCard);
  const [canEnterScores, setCanEnterScores] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasScoreCard || canEnterScores || (isTimelineLoading && !isError)) {
      return;
    }
    const timeout = window.setTimeout(
      () => setCanEnterScores(true),
      prefersReducedMotion() || isError ? 0 : SCORE_CARD_START_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [hasScoreCard, canEnterScores, isTimelineLoading, isError]);

  return (
    <div className="flex min-w-0 flex-col">
      {hasScoreCard && canEnterScores && (
        <ScoreCardEntry timelineRef={timelineRef}>{scoreCard}</ScoreCardEntry>
      )}
      <div ref={timelineRef} className="min-w-0">
        {isError ? (
          <p className="text-sm text-muted-foreground">
            Unable to load customer.
          </p>
        ) : isTimelineLoading ? (
          <CustomerTimelineSkeleton />
        ) : (
          timeline
        )}
      </div>
    </div>
  );
};

export const CustomerDetailPage: FC = () => {
  const routeParams = useParams();
  const accountId = asAccountId(requireRouteParam(routeParams, "accountId"));
  const playbookId = asPlaybookId(requireRouteParam(routeParams, "playbookId"));
  const { teammateId } = routeParams;
  const [selectedTeammateId, setSelectedTeammateId] =
    useState<TeammateId | null>(null);
  const [selectedPlaybookId, setSelectedPlaybookId] =
    useState<PlaybookId | null>(null);
  const detailResult = useCustomerDetailQuery(accountId);
  const timelineResult = useCustomerTimelineInfiniteQuery(accountId);
  const detail = detailResult.currentData;
  const pages = timelineResult.currentData?.pages;
  const playbookScores = detail?.playbook_scores;
  const calls = useMemo(
    () => pages?.flatMap((page) => page.calls) ?? [],
    [pages],
  );
  const { loadMoreSentinelRef } = useInfiniteScroll({
    entities: calls,
    onLoadMore:
      timelineResult.hasNextPage &&
      !timelineResult.isFetching &&
      !timelineResult.isError
        ? timelineResult.fetchNextPage
        : undefined,
  });
  const playbookOptions = useMemo<
    readonly SimpleSelectOption<PlaybookId | "all">[]
  >(
    () => [
      { value: "all", label: "All playbooks" },
      ...(detail?.playbooks ?? [])
        .map((playbook) => ({ value: playbook.id, label: playbook.name }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    ],
    [detail?.playbooks],
  );
  const teammateOptions = useMemo<
    readonly SimpleSelectOption<TeammateId | "all">[]
  >(() => {
    return [
      { value: "all", label: "All teammates" },
      ...(detail?.teammates ?? [])
        .map((teammate) => ({
          value: teammate.id,
          label: buildTeammateDisplayNames(teammate).primaryDisplayName,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [detail?.teammates]);

  const highlightedActivityIds = useMemo(() => {
    if (selectedPlaybookId === null && selectedTeammateId === null) {
      return undefined;
    }
    return new Set(
      calls
        .filter(
          (call) =>
            (selectedPlaybookId === null ||
              call.playbook_id === selectedPlaybookId) &&
            (selectedTeammateId === null ||
              call.teammates.some(
                (teammate) => teammate.id === selectedTeammateId,
              )),
        )
        .map((call) => call.id),
    );
  }, [calls, selectedPlaybookId, selectedTeammateId]);

  const modalNavigation = useModalNavigation();

  const activities: readonly CustomerTimelineActivity[] = calls.map((call) => ({
    id: call.id,
    dateTime: call.recorded_at,
    dateLabel: DateTime.fromISO(call.recorded_at).toFormat("MMM d, h:mm a"),
    icon: <IconPhone className="size-4" />,
    content: (
      <div className={PH_MASK_CLASS}>
        <CustomerTimelineCall
          title={call.title || "Call recording"}
          openCall={() =>
            modalNavigation.push({
              pathname: teammateId
                ? appPaths.teammateCustomerCallTranscript.build({
                    playbookId,
                    teammateId,
                    accountId,
                    gradingId: call.grading_id,
                  })
                : appPaths.playbookCustomerCallTranscript.build({
                    playbookId,
                    accountId,
                    gradingId: call.grading_id,
                  }),
              search: new URLSearchParams({
                recordingId: call.id,
              }).toString(),
            })
          }
          participants={call.teammates.map((teammate) => ({
            id: teammate.id,
            name: buildTeammateDisplayNames(teammate).primaryDisplayName,
            kind: teammateLabelKind(teammate.type),
          }))}
          duration={
            call.duration_seconds == null
              ? undefined
              : renderCallLength(call.duration_seconds)
          }
          gradingSummary={
            call.is_split ? (
              call.assessment ? (
                <GradingSectionSummariesContainer
                  assessment={call.assessment}
                  emptyState="Scoring not applicable"
                />
              ) : (
                "No score available"
              )
            ) : undefined
          }
          outcome={
            call.outcome ? (
              <OutcomeResultPill size={Size.SMALL} result={call.outcome} />
            ) : undefined
          }
        />
      </div>
    ),
  }));

  const isError =
    (detailResult.isError && !detail) || (timelineResult.isError && !pages);

  return (
    <PageLayout>
      <PageHeader
        title={
          <span className={PH_MASK_CLASS}>{detail?.name ?? "Customer"}</span>
        }
        isLoading={detailResult.isLoading}
        actions={
          detail && (detail.phone_number || detail.contact_email) ? (
            <CustomerInfoPopover
              phoneNumber={detail.phone_number}
              contactEmail={detail.contact_email}
            />
          ) : undefined
        }
        filterBar={
          <FilterBar
            isLoading={
              (timelineResult.isFetching &&
                !timelineResult.isFetchingNextPage) ||
              detailResult.isFetching
            }
            leadingItems={
              <SimpleSelect
                label="Playbook"
                isLabelHidden
                isDisabled={!detail}
                options={playbookOptions}
                value={selectedPlaybookId ?? "all"}
                onChange={(value) =>
                  setSelectedPlaybookId(value === "all" ? null : value)
                }
                size={Size.SMALL}
              />
            }
            items={
              <div className={twMerge("contents", PH_MASK_CLASS)}>
                <SimpleSelect
                  label="Teammate"
                  isLabelHidden
                  isDisabled={!detail}
                  options={teammateOptions}
                  value={selectedTeammateId ?? "all"}
                  onChange={(value) =>
                    setSelectedTeammateId(value === "all" ? null : value)
                  }
                  size={Size.SMALL}
                />
              </div>
            }
            onReset={
              selectedTeammateId !== null || selectedPlaybookId !== null
                ? () => {
                    setSelectedTeammateId(null);
                    setSelectedPlaybookId(null);
                  }
                : undefined
            }
          />
        }
      />
      <PageBody>
        <CustomerDetailContent
          key={accountId}
          isTimelineLoading={!pages}
          isError={isError}
          scoreCard={
            // Split rows carry no scores, so a card of only split rows has
            // nothing to show.
            playbookScores?.some((score) => !score.is_split) ? (
              <CustomerPlaybookScores
                scores={playbookScores}
                selectedPlaybookId={selectedPlaybookId}
                selectedTeammateId={selectedTeammateId}
                onSelect={(playbookId, teammateId) => {
                  setSelectedPlaybookId(playbookId);
                  setSelectedTeammateId(teammateId);
                }}
              />
            ) : null
          }
          timeline={
            <div className="[--customer-timeline-sticky-top:6.5rem] sm:[--customer-timeline-sticky-top:7.5rem]">
              <CustomerTimeline
                activities={activities}
                highlightedActivityIds={highlightedActivityIds}
                isLoadingMore={timelineResult.isFetchingNextPage}
              />
              {timelineResult.isError ? (
                <Button
                  variant={ButtonVariant.Secondary}
                  size={Size.SMALL}
                  className="mt-5"
                  onClick={() => void timelineResult.fetchNextPage()}
                >
                  Retry loading activity
                </Button>
              ) : timelineResult.hasNextPage ? (
                <div
                  ref={loadMoreSentinelRef}
                  className="mt-5 min-h-8"
                  aria-hidden="true"
                />
              ) : null}
            </div>
          }
        />
      </PageBody>
      <NavigationSlideoverContainer />
    </PageLayout>
  );
};
